import { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { useCommonServices, useExperimentService, useUserService } from '../services'
import { UrlBasedUsageTracker, toolsLtvExp3Limits } from '@lib/utils/tool-usage'
import { TrialModalContent } from '@components/ui-lib/modal/free-user-trial-modal'
import { isEligibleForTrial } from '@lib/helpers/trial-cta'
import { PageContext } from '@contexts/page-context'
import { gnarSpec } from '@lib/tracking/gnar'

interface UseToolsLtvExp3Experiment {
  usageTracker: UrlBasedUsageTracker | undefined
  showTrialModal: boolean
  setShowTrialModal: (show: boolean) => void
  isToolsLtvExp3Experiment: boolean
  trialModalContent: TrialModalContent
  trackModalShow: () => void
}

export const useToolsLtvExp3Experiment = (): UseToolsLtvExp3Experiment => {
  const { currentUser } = useUserService()
  const { facadeService } = useCommonServices()
  const { specialOffers } = facadeService.get()
  const { getToolsLtvExp3Assignment } = useExperimentService()
  const { pageId, slug } = useContext(PageContext)

  const [toolsLtvExp3Assignment] = useState(() => getToolsLtvExp3Assignment())
  const [showTrialModal, setShowTrialModal] = useState(false)

  const isToolsLtvExp3Experiment = toolsLtvExp3Assignment === 'limit_1' || toolsLtvExp3Assignment === 'limit_2'
  const isTrialEligible = isEligibleForTrial(specialOffers)
  const shouldShowUpgradeCopy = isToolsLtvExp3Experiment && !isTrialEligible

  const handleLimitReached = useCallback(() => {
    setShowTrialModal(true)
  }, [])

  const usageTracker = useMemo(() => {
    if (isToolsLtvExp3Experiment) {
      return new UrlBasedUsageTracker(toolsLtvExp3Limits[toolsLtvExp3Assignment], false, undefined, currentUser)
    }
    return undefined
  }, [isToolsLtvExp3Experiment, toolsLtvExp3Assignment, currentUser])

  useEffect(() => {
    if (usageTracker) {
      usageTracker.addEventListener('limitreached', handleLimitReached)
      return () => {
        usageTracker.removeEventListener('limitreached', handleLimitReached)
      }
    }
  }, [usageTracker, handleLimitReached])

  const ctaText = shouldShowUpgradeCopy ? 'Get Grammarly Pro' : 'Try Grammarly Pro for free'

  const description = shouldShowUpgradeCopy
    ? "You've reached your daily free limit. Upgrade to Pro to get full access to Grammarly."
    : "You've reached your daily free limit. Start your free trial today to get full access to Grammarly."

  const trackModalShow = useCallback(() => {
    if (isToolsLtvExp3Experiment) {
      const variation = shouldShowUpgradeCopy ? 'modal_trial_ineligible' : 'modal_trial_eligible'
      gnarSpec.cmsPersonalizedContentComponentShow(pageId, slug, 'tools_ltv_exp_3', variation)
    }
  }, [isToolsLtvExp3Experiment, shouldShowUpgradeCopy, pageId, slug])

  return {
    usageTracker,
    showTrialModal,
    setShowTrialModal,
    isToolsLtvExp3Experiment,
    trialModalContent: {
      ctaText,
      description,
      showDisclaimer: false,
    },
    trackModalShow,
  }
}
